import React, { useMemo, useState } from "react";
import { T, AGING_RAMP } from "../theme/tokens.js";
import { Panel, Empty, Kpi, Chip, Notice } from "../components/ui/index.jsx";
import RegisterTable from "../components/RegisterTable.jsx";
import DrillPanel from "../components/DrillPanel.jsx";
import { useRegisters } from "../lib/useRegister.js";
import {
  invoiceSummary, budgetPosture, AGING_BUCKETS, vendorTermsLookup,
  DEFAULT_TERMS_DAYS,
} from "../lib/calc.js";
import { fmtMoney, fmtDate } from "../lib/format.js";

function Money({ children }) {
  return <span style={{ whiteSpace: "nowrap" }}>{children}</span>;
}

function Table({ head, rows, note, onRow }) {
  return (
    <div style={{ overflowX: "auto" }}>
      <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
        <thead><tr>{head.map((h, i) => (
          <th key={h} style={{
            textAlign: i ? "right" : "left", padding: "7px 10px",
            borderBottom: `2px solid ${T.navy}`, color: T.navy, fontSize: 11,
            fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em",
            whiteSpace: "nowrap",
          }}>{h}</th>
        ))}</tr></thead>
        <tbody>{rows.map((r, i) => (
          <tr key={i} onClick={onRow ? () => onRow(i) : undefined}
            style={{ borderBottom: `1px solid ${T.line}`,
                     cursor: onRow ? "pointer" : "default" }}>
            {r.map((c, j) => (
              <td key={j} style={{
                padding: "7px 10px", textAlign: j ? "right" : "left",
                overflowWrap: j ? "normal" : "anywhere",
              }}>{c}</td>
            ))}
          </tr>
        ))}</tbody>
      </table>
      {note && <div style={{ fontSize: 12, color: T.soft, marginTop: 8 }}>{note}</div>}
    </div>
  );
}

/* A horizontal bar made of the aging buckets, so the shape of the problem
   reads before any number does. */
function AgingBar({ buckets, total, onOpen }) {
  if (!total) return null;
  return (
    <div style={{ display: "flex", height: 16, borderRadius: 3, overflow: "hidden",
                  border: `1px solid ${T.line}`, marginBottom: 10 }}>
      {AGING_BUCKETS.map((b) => {
        const share = buckets[b].dollars / total;
        if (share <= 0) return null;
        return (
          <div key={b} title={`${b}: ${fmtMoney(buckets[b].dollars)} across ${buckets[b].count}`}
            onClick={onOpen ? () => onOpen(b) : undefined}
            style={{ width: `${share * 100}%`, background: AGING_RAMP[b],
                     cursor: onOpen ? "pointer" : "default" }} />
        );
      })}
    </div>
  );
}

export default function Finance({ contract }) {
  const { data } = useRegisters(
    ["invoices", "vendors", "expenses", "resources"], contract?.id,
  );
  return <FinanceView contract={contract} data={data} />;
}

/* showRegisters is off in the visual preview, where the editable tables would
   try to reach a database that is not configured. Everything above them is
   pure and renders from the data it is given. */
export function FinanceView({ contract, data, showRegisters = true }) {
  const [drill, setDrill] = useState(null);
  const model = useMemo(() => {
    if (!data) return null;
    const invoices = invoiceSummary(data.invoices, {
      vendors: data.vendors, contractTerms: contract?.payment_terms_days,
    });
    const budget = budgetPosture(contract, data.expenses, data.resources);
    const lookup = vendorTermsLookup(data.vendors);

    /* Which vendors are being aged against a fallback rather than against
       terms anybody set. Aging is only as honest as its terms, so the screen
       says when it is guessing. */
    const named = new Set(data.invoices.map((i) => (i.vendor || "").trim()).filter(Boolean));
    const missingTerms = [...named].filter((v) => !lookup(v));

    return { invoices, budget, missingTerms };
  }, [data, contract]);

  if (!model) return <Empty text="Loading..." />;

  const { invoices: inv, budget, missingTerms } = model;
  const contractTerms = contract?.payment_terms_days || DEFAULT_TERMS_DAYS;

  /* Aging works on { invoice, aging } pairs. The drill panel wants a row, so
     the aging is folded back onto it and the panel can print days past
     without recomputing anything. */
  const asRows = (pairs) => pairs.map((p) => ({ ...p.invoice, _aging: p.aging }));
  const open = (title, pairs) =>
    setDrill(pairs?.length ? { kind: "invoices", title, rows: asRows(pairs) } : null);
  const inBucket = (b) => inv.open.filter((p) => (p.aging.bucket || "Within terms") === b);
  const clickable = { cursor: "pointer" };

  return (
    /* minmax(0, 1fr) rather than 1fr: a grid item defaults to a minimum
       width of its content, which lets a wide table or a long Gantt push
       the panel out past the page instead of scrolling inside it. */
    <div style={{ display: "grid", gap: 16, gridTemplateColumns: "minmax(0, 1fr)" }}>
      <div style={{ display: "grid", gap: 12,
                    gridTemplateColumns: "repeat(auto-fit, minmax(190px, 1fr))" }}>
        <div style={clickable} onClick={() => open("Open invoices", inv.open)}>
          <Kpi label="Open invoices" value={fmtMoney(inv.totalOutstanding)}
               sub={`${inv.open.length} unpaid`} />
        </div>
        <div style={clickable} onClick={() => open("Invoices past terms", inv.past)}>
          <Kpi label="Past terms" value={fmtMoney(inv.pastTermsDollars)}
               sub={`${inv.pastTermsCount} invoice${inv.pastTermsCount === 1 ? "" : "s"}`}
               color={inv.pastTermsCount ? T.risk : T.navy} />
        </div>
        <div style={clickable} onClick={() => open("Furthest past terms",
          inv.past.filter((p) => p.aging.pastDays === inv.oldestPastDays))}>
          <Kpi label="Oldest past terms" value={inv.oldestPastDays ? `${inv.oldestPastDays}d` : "None"}
               sub="Days beyond that invoice's own terms"
               color={inv.oldestPastDays > 60 ? T.risk : inv.oldestPastDays ? T.warn : T.navy} />
        </div>
        <Kpi label="Spent against ceiling"
             value={budget.percentUsed === null ? "No ceiling set" : `${budget.percentUsed}%`}
             sub={budget.ceiling ? `${fmtMoney(budget.spent)} of ${fmtMoney(budget.ceiling)}` : "Set a not-to-exceed on the contract"}
             color={budget.color} />
      </div>

      {drill && <DrillPanel drill={drill} onClose={() => setDrill(null)} />}

      <Panel title="Budget against the ceiling" accent={budget.color}>
        {!budget.ceiling ? (
          <Empty text="This contract has no not-to-exceed value yet. Set one on the Contracts page and the ceiling appears here." />
        ) : (
          <>
            <div style={{ height: 18, background: T.line + "66", borderRadius: 3,
                          overflow: "hidden", marginBottom: 10 }}>
              <div style={{
                width: `${Math.min(100, (budget.spent / budget.ceiling) * 100)}%`,
                height: "100%", background: budget.color,
              }} />
            </div>
            <Table
              head={["", "Amount"]}
              rows={[
                ["Expenses recorded", <Money>{fmtMoney(budget.spent)}</Money>],
                ["Labour, rate by hours used", <Money>{fmtMoney(budget.laborCost)}</Money>],
                ["Not-to-exceed ceiling", <Money>{fmtMoney(budget.ceiling)}</Money>],
                ["Remaining under the ceiling",
                  <Money>{fmtMoney(Math.max(0, budget.ceiling - budget.spent))}</Money>],
              ]}
              onRow={(i) => {
                /* The first two lines are made of records. The ceiling and
                   what is left of it are the contract's own numbers, so there
                   is nothing underneath them to open. */
                if (i === 0) setDrill(data.expenses.length
                  ? { kind: "expenses", title: "Expenses recorded", rows: data.expenses } : null);
                if (i === 1) setDrill(data.resources.length
                  ? { kind: "resources", title: "Labour by person", rows: data.resources } : null);
              }}
              note="Amber at 75 percent of the ceiling, red at 90. Labour is shown beside the total rather than inside it, because it is only spent once it reaches an expense or an invoice."
            />
          </>
        )}
      </Panel>

      <Panel title="Invoice aging" accent={inv.pastTermsCount ? T.risk : T.navy}
        right={<Chip color={T.steel}>Contract terms: Net {contractTerms}</Chip>}>
        {inv.open.length === 0 ? (
          <Empty text="No open invoices. Add them here, or import a statement once the invoice importer arrives." />
        ) : (
          <>
            <AgingBar buckets={inv.buckets} total={inv.totalOutstanding}
              onOpen={(b) => open(`Invoices ${b.toLowerCase()}`, inBucket(b))} />
            <Table
              head={["Bucket", "Invoices", "Dollars"]}
              rows={AGING_BUCKETS.map((b) => [
                <span><span style={{
                  display: "inline-block", width: 9, height: 9, borderRadius: 2,
                  background: AGING_RAMP[b], marginRight: 7,
                }} />{b}</span>,
                inv.buckets[b].count,
                <Money>{fmtMoney(inv.buckets[b].dollars)}</Money>,
              ])}
              onRow={(i) => open(`Invoices ${AGING_BUCKETS[i].toLowerCase()}`,
                inBucket(AGING_BUCKETS[i]))}
              note="Aging is measured against each invoice's own net terms, not a flat 30/60/90. An invoice takes its own terms first, then its vendor's, then this contract's, then 60 days. Click a bucket, a bar or a card to open the invoices behind it."
            />
          </>
        )}
        {missingTerms.length > 0 && (
          <div style={{ marginTop: 12 }}>
            <Notice kind="warn">
              No payment terms recorded for {missingTerms.slice(0, 6).join(", ")}
              {missingTerms.length > 6 ? ` and ${missingTerms.length - 6} more` : ""}.
              Those invoices are being aged at Net {contractTerms}, this contract's
              terms. Set each vendor's real terms on the Vendors tab and the aging
              corrects itself.
            </Notice>
          </div>
        )}
      </Panel>

      {inv.byVendor.length > 0 && (
        <Panel title="Past terms by vendor">
          <Table
            head={["Vendor", "Open", "Past terms", "Invoices past"]}
            rows={inv.byVendor.map((v) => [
              v.vendor,
              <Money>{fmtMoney(v.open)}</Money>,
              <Money style={{ color: v.past ? T.risk : T.ink }}>{fmtMoney(v.past)}</Money>,
              v.pastCount,
            ])}
            onRow={(i) => {
              const name = inv.byVendor[i].vendor;
              open(`${name}: open invoices`,
                inv.open.filter((p) => (p.invoice.vendor || "Unassigned") === name));
            }}
          />
        </Panel>
      )}

      {inv.worst.length > 0 && (
        <Panel title="Worst five open invoices" accent={T.risk}>
          <Table
            head={["Invoice", "Vendor", "Submitted", "Terms", "Days past", "Amount"]}
            rows={inv.worst.map((p) => [
              p.invoice.title,
              p.invoice.vendor || "Unassigned",
              fmtDate(p.invoice.submitted),
              `Net ${p.aging.termsDays}`,
              p.aging.pastDays,
              <Money>{fmtMoney(p.invoice.amount)}</Money>,
            ])}
            onRow={(n) => open(inv.worst[n].invoice.title, [inv.worst[n]])}
            note="Ordered by how far past its own terms each one is, which is not the same order as by size. Click one to open it."
          />
        </Panel>
      )}

      {showRegisters && (
        <>
      <Panel title="Invoices">
        <RegisterTable registerKey="invoices" contract={contract}
          note="Leave terms blank to use the vendor's, or the contract's." />
      </Panel>

      <Panel title="Vendors and their payment terms">
        <RegisterTable registerKey="vendors" contract={contract}
          note="Terms here drive the aging above for every invoice that does not carry its own." />
      </Panel>

      <Panel title="Expenses">
        <RegisterTable registerKey="expenses" contract={contract} />
      </Panel>

      <Panel title="Resources and rates">
        <RegisterTable registerKey="resources" contract={contract}
          note="Labour cost is rate multiplied by hours used." />
      </Panel>
        </>
      )}
    </div>
  );
}
